import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Reveal from "@/components/7fcc/Reveal";
import SectionHeader from "@/components/7fcc/SectionHeader";
import CTAButton from "@/components/7fcc/CTAButton";
import { HOME, APPLY_URL } from "@/data/homeContent";
import { EASE } from "@/lib/motion";

// Sales Readiness Assessment — exact questions, options, scoring and
// result copy from the original script.js. percent = round(step/6*100).
const QUESTIONS = HOME.assessment.questions;
const R = HOME.assessment.results;

export default function AssessmentWidget() {
  const a = HOME.assessment;
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  const picked = answers[QUESTIONS[step].key];
  const percent = done ? 100 : Math.round((step / QUESTIONS.length) * 100);

  const computeResult = () => {
    const readiness =
      (answers.hours?.val ?? 0) +
      (answers.comfort?.val ?? 0) +
      (answers.review?.val ?? 0) +
      (answers.coach?.val ?? 0);
    const path = answers.path?.val ?? "both";
    const comfort = answers.comfort?.val ?? 0;
    if (path === "closer" && readiness >= 6 && comfort >= 2) return R.closerDevelopment;
    if (readiness >= 6 && comfort >= 2) return R.setterCertification;
    return R.setterFoundations;
  };
  const result = done ? computeResult() : null;

  const pick = (option) => setAnswers((prev) => ({ ...prev, [QUESTIONS[step].key]: option }));

  return (
    <section id="assessment" className="bg-ink-900 px-6 py-16 md:py-24" data-testid="home-assessment">
      <div className="mx-auto max-w-3xl">
        <SectionHeader
          center
          eyebrow={a.eyebrow}
          title={a.headline}
          support={a.support}
          testId="home-assessment-heading"
        />
        <Reveal delay={0.15} className="mt-10">
          <div className="relative overflow-hidden rounded-lg border border-line bg-ink-950/70" data-testid="assessment-widget">
            {!done && (
              <div className="border-b border-line px-6 py-4 md:px-8" data-testid="assessment-progress">
                <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.22em] text-fg-dim">
                  <span data-testid="assessment-step-label">{`Question ${step + 1} of ${QUESTIONS.length}`}</span>
                  <span data-testid="assessment-progress-value">{percent}%</span>
                </div>
                <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-ink-800">
                  <div
                    className="h-full rounded-full bg-brand transition-all duration-500"
                    style={{ width: `${percent}%` }}
                    data-testid="assessment-progress-bar"
                  />
                </div>
              </div>
            )}

            <div className="relative flex min-h-[300px] items-center justify-center px-6 py-10 md:px-10">
              <AnimatePresence mode="wait">
                {!done ? (
                  <motion.div
                    key={`q-${step}`}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.35, ease: EASE }}
                    className="w-full max-w-md"
                    data-testid={`assessment-question-${step + 1}`}
                  >
                    <h3 className="text-center font-display text-xl font-medium text-fg md:text-2xl">
                      {QUESTIONS[step].q}
                    </h3>
                    <div className="mt-6 flex flex-col gap-3">
                      {QUESTIONS[step].options.map((option) => (
                        <button
                          key={option.label}
                          type="button"
                          onClick={() => pick(option)}
                          data-testid={`assessment-option-${step + 1}-${option.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className={`w-full rounded-lg border px-5 py-4 text-left text-sm transition-all duration-200 ${
                            picked?.label === option.label
                              ? "border-brand bg-brand/10 text-fg shadow-glow-soft"
                              : "border-line bg-ink-900/50 text-fg-muted hover:border-brand/60 hover:bg-brand/5 hover:text-fg"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <div className="mt-6 flex items-center justify-between">
                      <button
                        type="button"
                        disabled={step === 0}
                        onClick={() => setStep((s) => Math.max(0, s - 1))}
                        data-testid="assessment-back"
                        className={`text-xs font-semibold uppercase tracking-[0.2em] transition-colors ${
                          step === 0 ? "text-fg-dim/40" : "text-fg-dim hover:text-fg"
                        }`}
                      >
                        ← Back
                      </button>
                      <button
                        type="button"
                        disabled={picked === undefined}
                        onClick={() => (step === QUESTIONS.length - 1 ? setDone(true) : setStep((s) => s + 1))}
                        data-testid="assessment-next"
                        className={`text-xs font-semibold uppercase tracking-[0.2em] transition-colors ${
                          picked !== undefined ? "text-brand-light hover:text-fg" : "text-fg-dim/40"
                        }`}
                      >
                        {step === QUESTIONS.length - 1 ? "See My Result →" : "Next →"}
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="result"
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.35, ease: EASE }}
                    className="w-full max-w-lg text-center"
                    data-testid="assessment-result"
                  >
                    <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-light">
                      {a.resultHeading}
                    </p>
                    <h3 className="mt-3 font-display text-2xl font-medium text-fg md:text-3xl" data-testid="assessment-recommendation">
                      {result.title}
                    </h3>
                    <p className="mt-4 text-sm leading-relaxed text-fg-muted md:text-base" data-testid="assessment-result-text">
                      {result.text}
                    </p>
                    <div className="mt-7 flex flex-col items-center gap-4">
                      <CTAButton label={a.applyLabel} href={APPLY_URL} testId="assessment-apply" />
                      <button
                        type="button"
                        onClick={() => {
                          setAnswers({});
                          setStep(0);
                          setDone(false);
                        }}
                        className="text-xs uppercase tracking-[0.2em] text-fg-dim transition-colors hover:text-fg"
                        data-testid="assessment-retake"
                      >
                        {a.retake}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}